import type { PlayLifecycle } from "../features/analytics/playLifecycle";
/**
 * Web implementation of AudioPlayer using Shaka Player for HLS playback.
 * Drop-in replacement for AudioPlayer.ts (native) with identical public interface.
 */

import type { NativeAudio } from "../../../runtime/nativeAudio";
import { captureAudioPlaybackError } from "./audioSentry";

// ============================================================================
// Public types (must match AudioPlayer.ts exactly)
// ============================================================================

export interface AudioTrack {
  id: string;
  queueItemId?: string;
  url: string;
  title: string;
  artist: string;
  artwork?: string;
  durationMs?: number;
}

export interface AudioPlayerState {
  playbackState: "idle" | "loading" | "playing" | "paused" | "error";
  positionMs: number;
  durationMs: number;
  bufferedPositionMs: number;
  isPositionConfirmed: boolean;
  lastError?: {
    code: number;
    message: string;
    severity: "transient" | "fatal";
  } | null;
}

type StateChangeListener = (state: AudioPlayerState) => void;
type TrackEndedListener = () => void;

// ============================================================================
// Shaka one-time initialization (polyfills once, not per track)
// ============================================================================

function loadShakaModule() {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require("shaka-player/dist/shaka-player.ui");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cachedShaka: any | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getShakaOnce(
  loadModule: () => ReturnType<typeof loadShakaModule>,
): any {
  if (cachedShaka) {
    return cachedShaka;
  }

  const shakaModule = loadModule();
  const shaka = shakaModule.default || shakaModule;

  shaka.polyfill.installAll();

  if (!shaka.Player.isBrowserSupported()) {
    throw new Error("Shaka: browser not supported");
  }

  cachedShaka = shaka;
  return shaka;
}

// ============================================================================
// Temporary on-demand master (first play of a song still being published)
// ============================================================================

// Shaka loads `master.tmp.m3u8` as a live stream, and after its playlist gets
// #EXT-X-ENDLIST the media element still reports an infinite duration, so the
// browser clamps every seek to the end of the buffered audio. The final
// `master.m3u8` sits in the same directory (the signed token covers both) and
// is a plain seekable stream once it exists.
const TMP_MASTER_URL = /^([^?#]*)\/master\.tmp\.m3u8([?#].*)?$/;
const MASTER_PLAYLIST_MARKER = "#EXT-X-STREAM-INF";

// Plain string rewrite so the signed token in the path is never re-encoded.
function finalMasterUrlFor(url: string): string | null {
  const match = TMP_MASTER_URL.exec(url);
  return match ? `${match[1]}/master.m3u8${match[2] ?? ""}` : null;
}

async function isMasterPublished(url: string): Promise<boolean> {
  try {
    // no-store, like the playability poll: a 404 from before publication must
    // not be replayed from cache.
    const response = await fetch(url, { cache: "no-store" });
    if (response.status !== 200) return false;
    return (await response.text()).includes(MASTER_PLAYLIST_MARKER);
  } catch {
    return false;
  }
}

// ============================================================================
// AudioPlayer class (web implementation)
// ============================================================================

export class AudioPlayer {
  private listeners: Set<StateChangeListener> = new Set();
  private trackEndedListeners: Set<TrackEndedListener> = new Set();
  private currentTrackId: string | null = null;
  private currentQueueItemId: string | null = null;
  private currentUrl: string | null = null;
  // Invalidates pending work when a play, seek reload or rebuild takes over.
  private playGeneration = 0;
  // A reload owns its target and transport intent until it completes. Media
  // events from Shaka's unload/load must not override the user's commands.
  private reloadSeek: {
    targetMs: number;
    shouldPlay: boolean;
    loading: boolean;
    resumeVersion: number;
  } | null = null;
  private currentState: AudioPlayerState = {
    playbackState: "idle",
    positionMs: 0,
    durationMs: 0,
    bufferedPositionMs: 0,
    isPositionConfirmed: false,
  };

  // Shaka/audio engine fields
  private audioEl: HTMLAudioElement | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private shakaPlayer: any | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private shaka: any | null = null;
  private disposed = false;
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;
  private progressTimer: ReturnType<typeof setInterval> | null = null;

  // Track last progress values to avoid spamming listeners
  private lastPositionMs = 0;
  private lastDurationMs = 0;
  private lastBufferedMs = 0;

  constructor(
    _nativeAudio: NativeAudio | undefined,
    _lifecycle: PlayLifecycle,
    private readonly loadShaka = loadShakaModule,
  ) {}

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.listeners.clear();
    this.trackEndedListeners.clear();
    this.playGeneration += 1;
    this.reloadSeek = null;
    this.stopProgressTimer();
    this.removeEventListeners();
    this.audioEl?.pause();
    if (this.shakaPlayer) {
      Promise.resolve(this.shakaPlayer.destroy()).catch(console.error);
    }
    this.audioEl = null;
    this.shakaPlayer = null;
  }

  // --------------------------------------------------------------------------
  // Engine lifecycle
  // --------------------------------------------------------------------------

  private async ensureInitialized(): Promise<void> {
    if (this.disposed || this.isInitialized) {
      return;
    }

    // Share one init promise across concurrent calls
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInitialize();
    await this.initPromise;
  }

  private async doInitialize(): Promise<void> {
    try {
      this.shaka = getShakaOnce(this.loadShaka);

      // Create a detached audio element
      this.audioEl = document.createElement("audio");
      this.audioEl.preload = "auto";
      this.audioEl.crossOrigin = "anonymous";

      // Create Shaka player and attach to audio element
      this.shakaPlayer = new this.shaka.Player();
      await this.shakaPlayer.attach(this.audioEl);
      if (this.disposed) return;

      // Wire event listeners
      this.attachEventListeners();

      // Start progress timer
      this.startProgressTimer();

      this.isInitialized = true;
    } catch (error) {
      this.currentState.playbackState = "error";
      this.currentState.lastError = {
        code: -1,
        message:
          error instanceof Error
            ? error.message
            : "Failed to initialize player",
        severity: "fatal",
      };
      this.notifyListeners();
      throw error;
    }
  }

  private attachEventListeners(): void {
    if (!this.audioEl || !this.shakaPlayer) return;

    // Audio element events
    this.audioEl.addEventListener("playing", this.handlePlaying);
    this.audioEl.addEventListener("pause", this.handlePause);
    this.audioEl.addEventListener("waiting", this.handleWaiting);
    this.audioEl.addEventListener("loadstart", this.handleLoadStart);
    this.audioEl.addEventListener("ended", this.handleEnded);
    this.audioEl.addEventListener("error", this.handleAudioError);
    this.audioEl.addEventListener("durationchange", this.handleDurationChange);

    // Shaka player error event
    this.shakaPlayer.addEventListener("error", this.handleShakaError);
  }

  private removeEventListeners(): void {
    if (this.audioEl) {
      this.audioEl.removeEventListener("playing", this.handlePlaying);
      this.audioEl.removeEventListener("pause", this.handlePause);
      this.audioEl.removeEventListener("waiting", this.handleWaiting);
      this.audioEl.removeEventListener("loadstart", this.handleLoadStart);
      this.audioEl.removeEventListener("ended", this.handleEnded);
      this.audioEl.removeEventListener("error", this.handleAudioError);
      this.audioEl.removeEventListener(
        "durationchange",
        this.handleDurationChange,
      );
    }

    if (this.shakaPlayer) {
      this.shakaPlayer.removeEventListener("error", this.handleShakaError);
    }
  }

  // --------------------------------------------------------------------------
  // Event handlers (arrow functions to preserve `this`)
  // --------------------------------------------------------------------------

  private handlePlaying = (): void => {
    if (this.reloadSeek?.loading) return;
    this.currentState.playbackState = "playing";
    this.currentState.lastError = null;
    this.notifyListeners();
  };

  private handlePause = (): void => {
    if (this.reloadSeek?.loading) return;
    if (this.audioEl?.ended) return; // The ended event supplies the play boundary.
    // Only set to paused if not in error state
    if (this.currentState.playbackState !== "error") {
      this.currentState.playbackState = "paused";
      this.notifyListeners();
    }
  };

  private handleWaiting = (): void => {
    if (this.reloadSeek?.loading) return;
    this.currentState.playbackState = "loading";
    this.notifyListeners();
  };

  private handleLoadStart = (): void => {
    if (this.reloadSeek?.loading) return;
    this.currentState.playbackState = "loading";
    this.notifyListeners();
  };

  private handleEnded = (): void => {
    this.notifyTrackEnded();
  };

  private handleAudioError = (): void => {
    const errorMessage = this.audioEl?.error?.message || "Audio playback error";
    this.currentState.playbackState = "error";
    this.currentState.lastError = {
      code: -1,
      message: errorMessage,
      severity: "fatal",
    };
    this.notifyListeners();

    // Treat as fatal - rebuild engine
    this.rebuildEngine().catch(console.error);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleShakaError = (event: any): void => {
    const detail = event.detail || {};
    const errorMessage = detail.message || "Shaka player error";
    this.currentState.playbackState = "error";
    this.currentState.lastError = {
      code: -1,
      message: errorMessage,
      severity: "fatal",
    };
    this.notifyListeners();

    // Treat as fatal - rebuild engine
    this.rebuildEngine().catch(console.error);
  };

  private handleDurationChange = (): void => {
    if (this.audioEl && isFinite(this.audioEl.duration)) {
      this.currentState.durationMs = this.audioEl.duration * 1000;
      this.notifyListeners();
    }
  };

  // --------------------------------------------------------------------------
  // Progress timer (emulates AudioPro PROGRESS events)
  // --------------------------------------------------------------------------

  private startProgressTimer(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
    }

    this.progressTimer = setInterval(() => {
      if (!this.audioEl || this.reloadSeek != null) return;

      const positionMs = this.audioEl.currentTime * 1000;
      const durationMs = isFinite(this.audioEl.duration)
        ? this.audioEl.duration * 1000
        : 0;

      // Compute buffered position
      let bufferedMs = 0;
      if (this.audioEl.buffered.length > 0) {
        bufferedMs =
          this.audioEl.buffered.end(this.audioEl.buffered.length - 1) * 1000;
      }

      // Only notify if values actually changed
      if (
        positionMs !== this.lastPositionMs ||
        durationMs !== this.lastDurationMs ||
        bufferedMs !== this.lastBufferedMs ||
        !this.currentState.isPositionConfirmed
      ) {
        this.lastPositionMs = positionMs;
        this.lastDurationMs = durationMs;
        this.lastBufferedMs = bufferedMs;
        this.currentState.positionMs = positionMs;
        this.currentState.durationMs = durationMs;
        this.currentState.bufferedPositionMs = bufferedMs;
        this.currentState.isPositionConfirmed = true;
        this.notifyListeners();
      }
    }, 500);
  }

  private stopProgressTimer(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }

  // --------------------------------------------------------------------------
  // Fatal error recovery
  // --------------------------------------------------------------------------

  private async rebuildEngine(): Promise<void> {
    // Invalidate before the async destroy: a pending fetch can finish while
    // the old audio element and Shaka instance still exist.
    this.playGeneration += 1;
    this.reloadSeek = null;

    // Stop timer
    this.stopProgressTimer();

    // Remove listeners
    this.removeEventListeners();

    // Destroy Shaka player
    if (this.shakaPlayer) {
      try {
        await this.shakaPlayer.destroy();
      } catch {
        // Ignore destroy errors
      }
    }

    // Reset state
    this.audioEl = null;
    this.shakaPlayer = null;
    this.shaka = null;
    this.isInitialized = false;
    this.initPromise = null;
    this.lastPositionMs = 0;
    this.lastDurationMs = 0;
    this.lastBufferedMs = 0;

    // Re-initialize
    await this.ensureInitialized();
  }

  // --------------------------------------------------------------------------
  // Listener notification
  // --------------------------------------------------------------------------

  private notifyListeners(): void {
    const state = { ...this.currentState };
    this.listeners.forEach((listener) => listener(state));
  }

  private notifyTrackEnded(): void {
    this.trackEndedListeners.forEach((listener) => listener());
  }

  // --------------------------------------------------------------------------
  // Public API (must match native AudioPlayer.ts exactly)
  // --------------------------------------------------------------------------

  on(listener: StateChangeListener): () => void {
    this.listeners.add(listener);
    // Immediately invoke listener with current state (native behavior)
    listener({ ...this.currentState });
    return () => this.off(listener);
  }

  off(listener: StateChangeListener): void {
    this.listeners.delete(listener);
  }

  onTrackEnded(listener: TrackEndedListener): () => void {
    this.trackEndedListeners.add(listener);
    return () => this.offTrackEnded(listener);
  }

  offTrackEnded(listener: TrackEndedListener): void {
    this.trackEndedListeners.delete(listener);
  }

  get activeTrackId(): string | null {
    return this.currentTrackId;
  }

  get activeQueueItemId(): string | null {
    return this.currentQueueItemId;
  }

  getState(): AudioPlayerState {
    return { ...this.currentState };
  }

  play(track: AudioTrack, options?: { startTimeMs?: number }): void {
    if (!track.url || track.url.trim() === "") {
      throw new Error("Cannot play track with empty URL");
    }

    // If same track and paused, resume instead of reloading
    // But if startTimeMs is provided, force a reload (the caller wants to play from a specific position)
    if (
      this.currentTrackId === track.id &&
      this.currentQueueItemId === (track.queueItemId ?? null) &&
      this.currentState.playbackState === "paused" &&
      options?.startTimeMs == null
    ) {
      this.resume();
      return;
    }

    this.currentTrackId = track.id;
    this.currentQueueItemId = track.queueItemId ?? null;
    this.currentUrl = track.url;
    this.playGeneration += 1;
    this.reloadSeek = null;

    // Reset position and error for new track
    this.currentState.positionMs = options?.startTimeMs ?? 0;
    this.currentState.isPositionConfirmed = false;
    this.currentState.lastError = null;
    this.lastPositionMs = 0;

    console.audio?.log?.("AudioPlayer.web: playing track", track);

    // Load and play asynchronously
    const generation = this.playGeneration;
    this.loadAndPlay(track.url, generation)
      .then(() => {
        if (generation !== this.playGeneration) return;
        if (options?.startTimeMs != null && this.audioEl) {
          this.audioEl.currentTime = options.startTimeMs / 1000;
        }
      })
      .catch((error) => {
        if (generation !== this.playGeneration) return;
        console.error("AudioPlayer.web: play failed", error);
        this.currentState.playbackState = "error";
        this.currentState.lastError = {
          code: -1,
          message: error instanceof Error ? error.message : "Playback failed",
          severity: "fatal",
        };
        this.notifyListeners();
      });
  }

  private async loadAndPlay(url: string, generation: number): Promise<void> {
    await this.ensureInitialized();
    if (generation !== this.playGeneration) return;

    if (!this.shakaPlayer || !this.audioEl) {
      throw new Error("Player not initialized");
    }

    const audioEl = this.audioEl;
    const shakaPlayer = this.shakaPlayer;
    const isCurrent = () =>
      generation === this.playGeneration && this.audioEl === audioEl;

    // Set loading state
    this.currentState.playbackState = "loading";
    this.notifyListeners();

    // Unload previous content
    await shakaPlayer.unload();
    if (!isCurrent()) return;

    // Load new content
    await shakaPlayer.load(url);
    if (!isCurrent()) return;

    // Attempt to play
    try {
      await audioEl.play();
    } catch (error) {
      if (!isCurrent()) return;
      // Handle autoplay restriction
      if (error instanceof Error && error.name === "NotAllowedError") {
        this.currentState.playbackState = "error";
        this.currentState.lastError = {
          code: -1,
          message: "Autoplay blocked: user gesture required to start playback",
          severity: "fatal",
        };
        this.notifyListeners();
        throw error;
      }
      throw error;
    }
  }

  pause(): void {
    console.audio?.log?.("AudioPlayer.web: pause");
    if (this.reloadSeek) this.reloadSeek.shouldPlay = false;
    this.audioEl?.pause();
    // Also update state directly for snappy UI
    this.currentState.playbackState = "paused";
    this.notifyListeners();
  }

  resume(): void {
    console.audio?.log?.("AudioPlayer.web: resume");

    if (this.reloadSeek) {
      this.reloadSeek.shouldPlay = true;
      this.reloadSeek.resumeVersion += 1;
      if (this.reloadSeek.loading) {
        this.currentState.playbackState = "loading";
        this.notifyListeners();
        return;
      }
    }

    if (!this.audioEl) {
      return;
    }

    this.audioEl.play().catch((error) => {
      // Handle autoplay restriction
      if (error instanceof Error && error.name === "NotAllowedError") {
        this.currentState.playbackState = "error";
        this.currentState.lastError = {
          code: -1,
          message: "Autoplay blocked: user gesture required to resume playback",
          severity: "fatal",
        };
        this.notifyListeners();
      } else {
        console.error("AudioPlayer.web: resume failed", error);
      }
    });
  }

  seekTo(positionMs: number): void {
    // Update internal state immediately (native behavior for snappy UI)
    this.currentState.positionMs = positionMs;
    this.currentState.isPositionConfirmed = false;
    this.lastPositionMs = positionMs;
    this.notifyListeners();

    console.audio?.log?.("AudioPlayer.web: seekTo", positionMs);

    if (!this.audioEl) return;

    if (this.reloadSeek != null) {
      this.reloadSeek.targetMs = positionMs;
      return;
    }

    const finalUrl = this.unseekableTmpMasterFinalUrl(positionMs);
    if (finalUrl) {
      this.reloadSeek = {
        targetMs: positionMs,
        shouldPlay: this.currentState.playbackState !== "paused",
        loading: false,
        resumeVersion: 0,
      };
      this.reloadOnFinalMaster(finalUrl).catch((error) => {
        console.error("AudioPlayer.web: reload on final master failed", error);
      });
      return;
    }

    this.audioEl.currentTime = positionMs / 1000;
  }

  /**
   * The final master URL to reload on when the current stream is the temporary
   * master and the browser cannot seek to `positionMs`; null otherwise.
   */
  private unseekableTmpMasterFinalUrl(positionMs: number): string | null {
    if (!this.audioEl || !this.currentUrl) return null;
    if (isFinite(this.audioEl.duration)) return null;

    const seekable = this.audioEl.seekable;
    const seekableEndMs =
      seekable.length > 0 ? seekable.end(seekable.length - 1) * 1000 : 0;
    if (positionMs <= seekableEndMs) return null;

    return finalMasterUrlFor(this.currentUrl);
  }

  private async reloadOnFinalMaster(finalUrl: string): Promise<void> {
    let generation = this.playGeneration;
    const audioEl = this.audioEl;
    const shakaPlayer = this.shakaPlayer;
    const seek = this.reloadSeek;
    if (!audioEl || !shakaPlayer || !seek) {
      this.reloadSeek = null;
      return;
    }
    const isCurrent = () =>
      generation === this.playGeneration &&
      this.audioEl === audioEl &&
      this.shakaPlayer === shakaPlayer;

    try {
      const published = await isMasterPublished(finalUrl);
      if (!isCurrent()) return;

      if (!published) {
        // Still publishing: the audio past the buffer may not exist yet, so
        // let the browser clamp the seek as before.
        audioEl.currentTime = seek.targetMs / 1000;
        return;
      }

      // This load supersedes even the initial play() if the user sought
      // before it finished. Its completion must not reset our position/error.
      generation = ++this.playGeneration;
      const loadedAtMs = seek.targetMs;
      seek.loading = true;
      this.currentState.playbackState = seek.shouldPlay ? "loading" : "paused";
      this.notifyListeners();

      console.audio?.log?.("AudioPlayer.web: reloading on final master", {
        positionMs: loadedAtMs,
      });

      await shakaPlayer.load(finalUrl, loadedAtMs / 1000);
      if (!isCurrent()) return;

      this.currentUrl = finalUrl;
      if (seek.targetMs !== loadedAtMs) {
        audioEl.currentTime = seek.targetMs / 1000;
      }
      const appliedTargetMs = seek.targetMs;
      while (seek.shouldPlay) {
        const resumeVersion = seek.resumeVersion;
        try {
          await audioEl.play();
          break;
        } catch (error) {
          if (!isCurrent()) return;
          // pause() cancels a pending browser play() with AbortError. A later
          // resume is a fresh request; retry only for that explicit command.
          if (!(error instanceof Error) || error.name !== "AbortError")
            throw error;
          if (seek.shouldPlay && seek.resumeVersion === resumeVersion)
            throw error;
        }
      }
      if (!isCurrent()) return;
      // Commands can also arrive while the play() promise is pending.
      if (seek.targetMs !== appliedTargetMs) {
        audioEl.currentTime = seek.targetMs / 1000;
      }
      if (!seek.shouldPlay) audioEl.pause();
      this.currentState.playbackState = seek.shouldPlay ? "playing" : "paused";
      this.currentState.lastError = null;
      this.notifyListeners();
    } catch (error) {
      if (!isCurrent()) return;
      this.currentState.playbackState = "error";
      this.currentState.lastError = {
        code: -1,
        message: error instanceof Error ? error.message : "Playback failed",
        severity: "fatal",
      };
      captureAudioPlaybackError(
        "web.seek_reload_error",
        this.currentState.lastError.message,
        {
          trackId: this.currentTrackId,
          positionMs: seek.targetMs,
        },
      );
      this.notifyListeners();
    } finally {
      if (isCurrent()) this.reloadSeek = null;
    }
  }
}
